import {
  ChangeDetectionStrategy, Component, DestroyRef, ElementRef, HostListener, computed, effect, inject, signal, untracked, viewChild,
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { Router } from '@angular/router';
import {
  LucideAngularModule, Check, CircleX, CornerDownRight, Lightbulb, LockKeyhole, Lock, Loader, Undo2, WifiOff, ArrowRight,
} from 'lucide-angular';
import { AuthService } from '../../../features/auth/services/auth.service';
import { OperadorService } from '../../../core/operador/operador.service';
import { EmpresaLogoService } from '../../../core/services/empresa-logo.service';
import { SrPinDotsComponent, SrPinDotsEstado } from '../sr-pin/sr-pin-dots.component';
import { SrPinKeypadComponent } from '../sr-pin/sr-pin-keypad.component';

type Pantalla = 'entrar' | 'confirmar' | 'elegir' | 'exito';
type EstadoEntrada = 'espera' | 'validando' | 'error' | 'bloqueada' | 'sin-conexion' | 'fallo';
type ErrorElegir = '' | 'nom' | 'facil' | 'usado' | 'sin-conexion' | 'fallo';

/** Cada cuántos segundos de bloqueo se le pregunta al servidor si administración ya desbloqueó la tablet. */
const CONSULTA_BLOQUEO_S = 10;

/**
 * sr-operador-gate · La pantalla del PIN de la tablet de taller.
 *
 * Tapa todo el trabajo hasta que un mecánico pone su PIN: entrar, confirmar «Hola, Pedro Soto», elegir el PIN propio la primera
 * vez, y el bloqueo tras 30 minutos sin uso. También muestra el aviso del minuto 29. Si el taller no usa PIN, no dibuja nada.
 *
 * Un PIN equivocado dice cuántos intentos quedan; al quinto la tablet se bloquea 5 minutos y la pantalla dice a quién pedirle
 * ayuda. Mientras dura, pregunta cada {@link CONSULTA_BLOQUEO_S} s si administración ya la desbloqueó desde Trabajadores.
 * Los textos se aclararon tras el primer bloqueo en producción (Fixes, 22-oct): Samuel autorizó que difieran del diseño.
 *
 * El PIN se pone con los botones grandes (tablet con guantes) o con el teclado del computador: números y Borrar.
 *
 * Diseño: docs/servicio-tecnico/diseno/SoftRent Modo Tecnico PIN.html. Reglas: docs/servicio-tecnico/PROPUESTA-MECANICO-POR-PIN.md.
 */
@Component({
  selector: 'sr-operador-gate',
  standalone: true,
  imports: [NgTemplateOutlet, LucideAngularModule, SrPinDotsComponent, SrPinKeypadComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './sr-operador-gate.component.html',
  styleUrl: './sr-operador-gate.component.scss',
})
export class SrOperadorGateComponent {
  protected readonly operador = inject(OperadorService);
  private readonly auth = inject(AuthService);
  protected readonly logo = inject(EmpresaLogoService);
  private readonly router = inject(Router);
  private readonly raiz = viewChild<ElementRef<HTMLElement>>('raiz');

  protected readonly icons = { Check, CircleX, CornerDownRight, Lightbulb, LockKeyhole, Lock, Loader, Undo2, WifiOff, ArrowRight };

  protected readonly digitos = signal('');
  protected readonly estado = signal<EstadoEntrada>('espera');
  protected readonly segundos = signal(0);
  protected readonly paso = signal<1 | 2>(1);
  protected readonly errorElegir = signal<ErrorElegir>('');
  protected readonly exito = signal(false);
  protected readonly guardando = signal(false);
  protected readonly teclaActiva = signal<string | null>(null);
  /** Cuántos PIN más se pueden probar antes del bloqueo, según el último rechazo; null si el servidor no lo dijo. */
  protected readonly intentosRestantes = signal<number | null>(null);

  private primero = '';
  /** El PIN temporal que se acaba de usar para entrar: no puede ser el nuevo. Solo vive en memoria. */
  private pinTemporal = '';
  private cuentaAtras: ReturnType<typeof setInterval> | null = null;
  private resalte: ReturnType<typeof setTimeout> | null = null;

  protected readonly pantalla = computed<Pantalla | null>(() => {
    if (this.exito()) return 'exito';
    if (!this.operador.activa()) return null;
    if (this.operador.pendiente()) return 'confirmar';
    const o = this.operador.operador();
    if (o?.debeCambiarPin) return 'elegir';
    if (!o || this.operador.bloqueado()) return 'entrar';
    return null;
  });

  protected readonly porInactividad = computed(() => this.operador.bloqueado() && this.operador.operador() !== null);
  protected readonly cuenta = computed(() => {
    const u = this.auth.getCurrentUser();
    return (u?.username as string | undefined) || (u?.nombre as string | undefined) || 'Taller';
  });
  protected readonly empresa = computed(() => (this.auth.getCurrentUser()?.empresaNombre as string | undefined) ?? '');
  protected readonly nombre = computed(() => this.operador.pendiente()?.nombre ?? this.operador.operador()?.nombre ?? '');
  protected readonly cargo = computed(() => this.operador.pendiente()?.cargo || 'Mecánico');
  protected readonly iniciales = computed(() =>
    this.nombre().split(/\s+/).filter(Boolean).slice(0, 2).map(p => p[0]?.toUpperCase()).join(''));
  protected readonly sigueEnOrden = computed(() => this.router.url.includes('/ejecutar'));
  protected readonly cuentaRegresiva = computed(() => {
    const s = Math.max(0, this.segundos());
    return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
  });
  protected readonly minutos = computed(() => Math.max(1, Math.ceil(this.segundos() / 60)));
  protected readonly textoIncorrecto = computed(() => {
    const quedan = this.intentosRestantes();
    if (quedan === null) return 'PIN incorrecto. Prueba otra vez.';
    return quedan === 1 ? 'PIN incorrecto. Te queda 1 intento.' : `PIN incorrecto. Te quedan ${quedan} intentos.`;
  });
  protected readonly estadoPuntos = computed<SrPinDotsEstado>(() => {
    if (this.pantalla() === 'elegir') return this.errorElegir() && this.errorElegir() !== 'sin-conexion' ? 'error' : 'normal';
    switch (this.estado()) {
      case 'validando': return 'validando';
      case 'error': return 'error';
      case 'bloqueada': return 'tenue';
      case 'sin-conexion': return 'tenue';
      default: return 'normal';
    }
  });
  protected readonly llenos = computed(() => this.estado() === 'validando' ? 4 : this.digitos().length);
  protected readonly teclasApagadas = computed(() =>
    this.estado() === 'validando' || this.estado() === 'bloqueada' || this.guardando());

  constructor() {
    inject(DestroyRef).onDestroy(() => { this.pararCuenta(); if (this.resalte) clearTimeout(this.resalte); });
    // Al abrirse la pantalla se suelta el foco del trabajo de atrás: si había un campo enfocado, los números del PIN se escribirían ahí.
    effect(() => {
      if (this.pantalla() !== null) (document.activeElement as HTMLElement | null)?.blur?.();
    });
    // Cada vez que la pantalla cambia de paso, se empieza limpio.
    effect(() => {
      this.pantalla();
      this.digitos.set('');
    });
    // La tablet ya venía bloqueada (se recargó durante el bloqueo): la cuenta regresiva aparece sin que nadie tenga que probar.
    effect(() => {
      const hasta = this.operador.bloqueadaHastaMs();
      untracked(() => {
        if (hasta !== null && hasta > Date.now() && this.estado() !== 'bloqueada') {
          this.iniciarCuenta(Math.ceil((hasta - Date.now()) / 1000));
        }
      });
    });
  }

  // ── Teclado del computador ──────────────────────────────────────────────

  @HostListener('document:keydown', ['$event'])
  protected alTeclado(e: KeyboardEvent): void {
    const pantalla = this.pantalla();
    if (pantalla === null) return;
    if (e.ctrlKey || e.metaKey || e.altKey) return;

    if (e.key === 'Tab') {
      // Con la pantalla abierta el foco no se va al trabajo de atrás.
      const raiz = this.raiz()?.nativeElement;
      if (raiz && !raiz.contains(document.activeElement)) {
        e.preventDefault();
        raiz.querySelector<HTMLElement>('button:not([disabled])')?.focus();
      }
      return;
    }
    if (pantalla === 'confirmar') {
      if (e.key === 'Enter' && !(e.target instanceof HTMLButtonElement)) { e.preventDefault(); void this.soyYo(); }
      return;
    }
    if (pantalla === 'exito') {
      if (e.key === 'Enter' && !(e.target instanceof HTMLButtonElement)) { e.preventDefault(); this.seguir(); }
      return;
    }
    if (/^[0-9]$/.test(e.key)) {
      e.preventDefault();
      this.resaltar(e.key);
      this.poner(e.key);
    } else if (e.key === 'Backspace') {
      e.preventDefault();
      this.resaltar('Backspace');
      this.borrar();
    }
  }

  private resaltar(tecla: string): void {
    this.teclaActiva.set(tecla);
    if (this.resalte) clearTimeout(this.resalte);
    this.resalte = setTimeout(() => this.teclaActiva.set(null), 120);
  }

  // ── Poner el PIN ────────────────────────────────────────────────────────

  protected poner(d: string): void {
    if (this.teclasApagadas()) return;
    if (this.pantalla() === 'entrar' && this.estado() !== 'espera') this.estado.set('espera');
    if (this.pantalla() === 'elegir') this.errorElegir.set('');
    if (this.digitos().length >= 4) return;
    this.digitos.update(v => v + d);
    if (this.digitos().length === 4) {
      if (this.pantalla() === 'entrar') void this.entrar();
      else if (this.pantalla() === 'elegir') void this.elegir();
    }
  }

  protected borrar(): void {
    if (this.teclasApagadas()) return;
    if (this.pantalla() === 'entrar' && this.estado() !== 'espera') this.estado.set('espera');
    if (this.pantalla() === 'elegir') this.errorElegir.set('');
    this.digitos.update(v => v.slice(0, -1));
  }

  private async entrar(): Promise<void> {
    const pin = this.digitos();
    this.estado.set('validando');
    const r = await this.operador.entrar(pin);
    this.digitos.set('');
    if (r.ok) {
      this.estado.set('espera');
      const nuevo = this.operador.pendiente();
      this.pinTemporal = nuevo?.debeCambiarPin ? pin : '';
      // Quien vuelve tras el bloqueo es el mismo de antes: no hace falta que confirme que es él.
      if (nuevo && !nuevo.debeCambiarPin && nuevo.trabajadorId === this.operador.idActual()) await this.soyYo();
      return;
    }
    switch (r.motivo) {
      case 'incorrecto': this.intentosRestantes.set(r.intentosRestantes); this.estado.set('error'); break;
      case 'bloqueada': this.iniciarCuenta(r.segundos); break;
      case 'sin-conexion': this.estado.set('sin-conexion'); break;
      default: this.estado.set('fallo');
    }
  }

  protected async soyYo(): Promise<void> {
    await this.operador.confirmar();
    this.paso.set(1);
    this.primero = '';
    this.errorElegir.set('');
    this.digitos.set('');
  }

  protected async noSoyYo(): Promise<void> {
    this.pinTemporal = '';
    await this.operador.rechazar();
    this.estado.set('espera');
  }

  // ── Elegir el PIN propio ────────────────────────────────────────────────

  private async elegir(): Promise<void> {
    const pin = this.digitos();
    if (this.paso() === 1) {
      if (pin === this.pinTemporal) {
        this.digitos.set('');
        this.errorElegir.set('usado');
        return;
      }
      this.primero = pin;
      this.digitos.set('');
      this.paso.set(2);
      return;
    }
    if (pin !== this.primero) {
      this.primero = '';
      this.digitos.set('');
      this.paso.set(1);
      this.errorElegir.set('nom');
      return;
    }
    this.guardando.set(true);
    const r = await this.operador.elegirPin(pin);
    this.guardando.set(false);
    this.digitos.set('');
    if (r === 'ok') {
      this.pinTemporal = '';
      this.primero = '';
      this.paso.set(1);
      this.exito.set(true);
      return;
    }
    this.primero = '';
    this.paso.set(1);
    this.errorElegir.set(r === 'facil' ? 'facil' : r === 'no-disponible' ? 'usado' : r === 'sin-conexion' ? 'sin-conexion' : 'fallo');
  }

  protected seguir(): void {
    this.exito.set(false);
  }

  // ── Demasiados intentos ─────────────────────────────────────────────────

  private iniciarCuenta(segundos: number): void {
    this.pararCuenta();
    this.segundos.set(segundos);
    this.estado.set('bloqueada');
    let tic = 0;
    this.cuentaAtras = setInterval(() => {
      // Administración la puede desbloquear antes: si el servidor ya no la tiene bloqueada, el próximo tic la suelta.
      if (++tic % CONSULTA_BLOQUEO_S === 0) void this.operador.revisarBloqueo();
      const hasta = this.operador.bloqueadaHastaMs();
      const restante = hasta === null ? 0 : Math.ceil((hasta - Date.now()) / 1000);
      this.segundos.set(Math.max(0, restante));
      if (restante <= 0) {
        this.pararCuenta();
        this.estado.set('espera');
      }
    }, 1000);
  }

  private pararCuenta(): void {
    if (this.cuentaAtras) clearInterval(this.cuentaAtras);
    this.cuentaAtras = null;
  }

  // ── Aviso del minuto 29 ─────────────────────────────────────────────────

  protected seguirTrabajando(): void {
    void this.operador.seguir();
  }
}
